import React from 'react';

import { DialogActions, DialogContent, Typography } from '@mui/material';
import { gridFilterModelSelector, useGridSelector } from '@mui/x-data-grid-premium';
import { AlineButton, AlineDialog, AlineTooltip } from 'src/components';
import { useStable } from 'src/editor/helpers/useStable';
import { useReportParams } from 'src/helpers';
import {
  getVisibleDocumentIds,
  useGetExtractionQuestions,
  useGetSelectedReportRows,
  useGetVisibleQuestionColumns,
  useRunExtractionJobsForDocuments,
  useStartReportExtraction,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  dialogPaper: {
    width: 640,
  },
  submitButton: {
    marginLeft: theme.spacing(1),
  },
}));

interface RunAIDialogProps {
  onClose: () => void;
  open: boolean;
  rowCount: number;
}

/**
 * Dialog for confirming AI extraction on report documents.
 * Rendered in ReportDataGrid to have access to rowCount and grid state.
 */
export function RunAIDialog({ onClose, open, rowCount }: RunAIDialogProps): JSX.Element {
  const { classes } = useStyles();
  const { reportId } = useReportParams();
  const { visibleQuestionColumns } = useGetVisibleQuestionColumns();
  const { apiRef, selectedReportRows } = useGetSelectedReportRows();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { extractionQuestions } = useGetExtractionQuestions({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { loading: startingExtractionJobs, startExtractionJobsForDocuments } =
    useRunExtractionJobsForDocuments();
  const { loading: startingReportExtraction, startReportExtraction } = useStartReportExtraction();

  const primaryDisabledReason = !extractionQuestions.length
    ? 'Add questions to your account to run Aline AI'
    : '';
  const documentsString = selectedReportRows.length
    ? `${selectedReportRows.length} selected ${selectedReportRows.length === 1 ? 'document' : 'documents'}`
    : `${rowCount} ${rowCount === 1 ? 'document' : 'documents'}`;

  async function handleRunAI(): Promise<void> {
    if (!selectedReportRows.length) {
      // Run AI extraction on the entire (filtered) report.
      await startReportExtraction({
        variables: {
          input: {
            filterModel,
            filterTimeZone,
            reportId,
            visibleDocumentIds: getVisibleDocumentIds(apiRef.current),
          },
        },
      });
      onClose();
      return;
    }

    /**
     * `row.id` is the grid row ID (composite in grouped/tree mode), `row.documentId`
     * is always the real document UUID. Mutation needs UUIDs; selectRows needs grid IDs.
     */
    const selectedRowIds = selectedReportRows.map(row => row.id);
    const documentIds = selectedReportRows.map(row => row.documentId);

    await startExtractionJobsForDocuments({
      variables: {
        input: { documentIds, questionIds: visibleQuestionColumns.map(col => col.field) },
      },
    });
    onClose();
    // Clear the selection only after the run is queued, so a failed run keeps it.
    apiRef.current.selectRows(selectedRowIds, false);
  }

  return (
    <AlineDialog
      classes={{ paper: classes.dialogPaper }}
      open={open}
      title="Run AI questions?"
      onClose={onClose}
    >
      <DialogContent className={classes.dialogContent}>
        <Typography>
          {`Are you sure you want to run all questions for ${documentsString}? This action cannot be undone.`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineTooltip arrow placement="top" title={primaryDisabledReason}>
          <div className={classes.submitButton}>
            <AlineButton
              color="primary"
              disabled={Boolean(primaryDisabledReason)}
              loading={startingExtractionJobs || startingReportExtraction}
              variant="contained"
              onClick={handleRunAI}
            >
              Run
            </AlineButton>
          </div>
        </AlineTooltip>
      </DialogActions>
    </AlineDialog>
  );
}
